import { type ReactNode } from "react";
import { Link } from "react-router-dom";
import type { Child, Report } from "../types";
import { ageDisplay } from "../types";

type Props = {
  child: Child;
  report: Report | null;
  // Pre-fetched download URL when status=rendered. Parent (MonthBoard)
  // resolves these in batch on refresh so each row doesn't issue its own
  // Storage SDK call.
  pdfUrl: string | null;
  onPickPhotos: (childId: string) => void;
  onRegenerate: (reportId: string) => void;
};

function statusLabel(report: Report | null): string {
  if (!report) return "+ Photos";
  if (report.status === "draft" && report.photos.length === 0) return "+ Photos";
  if (report.status === "draft" && report.content.story_en === "") return "Needs draft";
  if (report.status === "draft") return "Edit draft";
  if (report.status === "approved") return "Approved";
  if (report.status === "rendered") return "PDF ✓";
  return "?";
}

export function ChildRow({ child, report, pdfUrl, onPickPhotos, onRegenerate }: Props) {
  const status = statusLabel(report);

  let action: ReactNode;
  if (status === "+ Photos") {
    action = (
      <button onClick={() => onPickPhotos(child.id)} className="text-[#ff6b35] font-medium">
        + Photos
      </button>
    );
  } else if (report && (status === "Needs draft" || status === "Edit draft" || status === "Approved")) {
    action = (
      <span className="flex gap-3 items-center">
        <button
          onClick={() => onPickPhotos(child.id)}
          className="text-gray-400"
          title="Re-pick photos"
          aria-label="Re-pick photos"
        >
          ↻
        </button>
        {report.photos.length > 0 && (
          <button
            onClick={() => onRegenerate(report.id)}
            className="text-gray-500 text-xs underline"
            title="Regenerate AI draft (overwrites current content)"
            aria-label="Regenerate AI draft"
          >
            redraft
          </button>
        )}
        <Link to={`/edit/${report.id}`} className="text-[#ff6b35] font-medium">
          {status} →
        </Link>
      </span>
    );
  } else if (report && status === "PDF ✓") {
    action = (
      <span className="flex gap-3 items-center">
        <button
          onClick={() => onPickPhotos(child.id)}
          className="text-gray-400 text-xs"
          title="Re-pick photos"
          aria-label="Re-pick photos"
        >
          ↻
        </button>
        <button
          onClick={() => onRegenerate(report.id)}
          className="text-gray-500 text-xs underline"
          title="Regenerate AI draft (resets to draft, overwrites current content)"
          aria-label="Regenerate AI draft"
        >
          redraft
        </button>
        <Link to={`/edit/${report.id}`} className="text-gray-500 text-xs">
          edit
        </Link>
        {pdfUrl ? (
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#ff6b35] font-medium"
          >
            PDF ↓
          </a>
        ) : (
          <span className="text-gray-400 text-sm">loading…</span>
        )}
      </span>
    );
  } else {
    action = <span className="text-gray-500 text-sm">{status}</span>;
  }

  return (
    <li className="flex justify-between items-center p-3 border-b">
      <span>
        <span className="font-medium">{child.name_en}</span>
        <span className="text-gray-400 text-sm ml-2">({ageDisplay(child.date_of_birth)})</span>
      </span>
      {action}
    </li>
  );
}
